import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';

// =============================================
// Create Rectificativa from Original Invoice
// - Creates new invoice of tipo='rectificativa' with negative amounts
// - Links to original via factura_rectificada_id
// - Marks original as 'rectificada'
// =============================================

async function generateNextNumber(serieId: string): Promise<{ numero: string; nextSeq: number }> {
  const { data: serie, error } = await supabaseAdmin
    .from('invoice_series')
    .select('*')
    .eq('id', serieId)
    .single();

  if (error || !serie) {
    throw new Error('Serie no encontrada');
  }

  if (!serie.activa) {
    throw new Error('La serie no está activa');
  }

  const seq = serie.siguiente_numero ?? 1;
  const prefijo = serie.prefijo ?? '';
  const paddedNum = String(seq).padStart(5, '0');
  const numero = `${prefijo}${paddedNum}`;

  return { numero, nextSeq: seq + 1 };
}

export default withAuth(async (req, res, user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  try {
    const { invoice_id, motivo } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }
    if (!motivo || typeof motivo !== 'string' || motivo.trim().length === 0) {
      return res.status(400).json({ error: 'motivo de rectificación es requerido' });
    }

    // Fetch original invoice
    const { data: original, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !original) {
      return res.status(404).json({ error: 'Factura original no encontrada' });
    }

    // Validate original state - must be emitida, enviada, or pagada
    const allowedStates = ['emitida', 'enviada', 'pagada'];
    if (!allowedStates.includes(original.estado)) {
      return res.status(400).json({
        error: `No se puede rectificar una factura en estado '${original.estado}'. Solo facturas emitidas, enviadas o pagadas.`,
      });
    }

    // Don't allow double rectification
    if (original.tipo === 'rectificativa') {
      return res.status(400).json({ error: 'No se puede rectificar una factura rectificativa' });
    }

    // Fetch original lines
    const { data: originalLines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', invoice_id)
      .order('orden', { ascending: true });

    // Find rectificativa series for this empresa, or use same series
    const { data: rectSeries } = await supabaseAdmin
      .from('invoice_series')
      .select('*')
      .eq('empresa_id', original.empresa_id)
      .eq('tipo', 'rectificativa')
      .eq('activa', true)
      .limit(1);

    const serieId = (rectSeries && rectSeries.length > 0) ? rectSeries[0].id : original.serie_id;

    // Generate new number
    let numero: string;
    let nextSeq: number;
    try {
      const result = await generateNextNumber(serieId);
      numero = result.numero;
      nextSeq = result.nextSeq;
    } catch (err: any) {
      return res.status(400).json({ error: err.message });
    }

    // Create rectificativa invoice with negative totals
    const rectInvoice = {
      empresa_id: original.empresa_id,
      serie_id: serieId,
      numero,
      tipo: 'rectificativa',
      estado: 'borrador',
      cliente_nombre: original.cliente_nombre,
      cliente_nif: original.cliente_nif,
      cliente_direccion: original.cliente_direccion,
      cliente_email: original.cliente_email,
      cliente_telefono: original.cliente_telefono,
      fecha_emision: new Date().toISOString().split('T')[0],
      fecha_vencimiento: null,
      fecha_operacion: null,
      moneda: original.moneda,
      tipo_cambio: original.tipo_cambio,
      // Negative amounts for rectificativa
      base_imponible: -Math.abs(+original.base_imponible),
      total_impuestos: -Math.abs(+original.total_impuestos),
      total_retenciones: -Math.abs(+original.total_retenciones),
      total_suplidos: -Math.abs(+original.total_suplidos),
      total_factura: -Math.abs(+original.total_factura),
      tipo_operacion: original.tipo_operacion,
      clave_regimen: original.clave_regimen,
      factura_rectificada_id: original.id,
      motivo_rectificacion: motivo.trim(),
      es_recurrente: false,
      recurrente_config: null,
      generada_desde: null,
      verifactu_enviada: false,
      verifactu_id: null,
      verifactu_qr: null,
      verifactu_fecha_envio: null,
      verifactu_estado: null,
      face_enviada: false,
      face_numero_registro: null,
      face_estado: null,
      email_enviado: false,
      email_fecha_envio: null,
      pdf_url: null,
      pdf_generado: false,
      notas: `Rectificativa de factura ${original.numero}. Motivo: ${motivo.trim()}`,
      notas_internas: null,
      created_by: user.id,
    };

    const { data: createdInvoice, error: insertError } = await supabaseAdmin
      .from('invoices')
      .insert(rectInvoice)
      .select()
      .single();

    if (insertError) {
      console.error('[API rectificativa] Insert error:', insertError);
      return res.status(500).json({ error: `Error al crear rectificativa: ${insertError.message}` });
    }

    // Copy lines with negative amounts
    if (originalLines && originalLines.length > 0) {
      const newLines = originalLines.map((line: Record<string, unknown>) => ({
        invoice_id: createdInvoice.id,
        orden: line.orden,
        concepto: line.concepto,
        descripcion: line.descripcion,
        cantidad: -(Math.abs(line.cantidad as number)),
        precio_unitario: line.precio_unitario,
        descuento_porcentaje: line.descuento_porcentaje,
        base_linea: -(Math.abs(line.base_linea as number)),
        tipo_iva: line.tipo_iva,
        porcentaje_iva: line.porcentaje_iva,
        cuota_iva: -(Math.abs(line.cuota_iva as number)),
        recargo_porcentaje: line.recargo_porcentaje,
        recargo_cuota: line.recargo_cuota ? -(Math.abs(line.recargo_cuota as number)) : null,
        retencion_porcentaje: line.retencion_porcentaje,
        retencion_cuota: line.retencion_cuota ? -(Math.abs(line.retencion_cuota as number)) : null,
        es_suplido: line.es_suplido,
        suplido_nif_tercero: line.suplido_nif_tercero,
      }));

      const { error: linesError } = await supabaseAdmin.from('invoice_lines').insert(newLines);

      if (linesError) {
        console.error('[API rectificativa] Lines insert error:', linesError);
        await supabaseAdmin.from('invoices').delete().eq('id', createdInvoice.id);
        return res.status(500).json({ error: `Error al copiar líneas: ${linesError.message}` });
      }
    }

    // Update series siguiente_numero
    await supabaseAdmin
      .from('invoice_series')
      .update({ siguiente_numero: nextSeq })
      .eq('id', serieId);

    // Mark original invoice as rectificada
    await supabaseAdmin
      .from('invoices')
      .update({ estado: 'rectificada', updated_at: new Date().toISOString() })
      .eq('id', invoice_id);

    // Return full rectificativa with lines
    const { data: lines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', createdInvoice.id)
      .order('orden', { ascending: true });

    return res.status(201).json({
      success: true,
      data: { ...createdInvoice, lines: lines ?? [] },
      originalInvoiceId: invoice_id,
      originalInvoiceNumber: original.numero,
    });
  } catch (error: any) {
    console.error('[API rectificativa] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});
